import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { EsraPipingAPIClient, ApplicationViewModel, BusinessUnitViewModel, SpecificationViewModel, SpecificationType } from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

@Component({
    selector: 'app-specification-add-edit',
    templateUrl: './specification-add-edit.component.html',
    styleUrls: ['./specification-add-edit.component.scss'],
    standalone: false
})
export class SpecificationAddEditComponent implements OnInit {
  inputFormGroup: UntypedFormGroup;
  specificationToUpdate: SpecificationViewModel | null = null;

  title: string = '';
  genericMissingValueMessage: string = 'This is a required field';

  isEditMode: boolean = false;
  isCreateMode: boolean = false;
  submitClicked: boolean = false;

  /** True while the API save call is in-flight — prevents double-submission. */
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** Inline API-level error message (e.g. duplicate SpecificationNumber). */
  apiErrorMessage: string | null = null;

  /** Dropdown options for the Type field. Maps enum values to display labels. */
  readonly typeOptions: { value: SpecificationType; label: string }[] = [
    { value: SpecificationType._0, label: 'Piping' },
    { value: SpecificationType._1, label: 'Valve' },
  ];

  /** Active business units loaded from the API for the BU dropdown. */
  businessUnits: BusinessUnitViewModel[] = [];

  /** Active applications loaded from the API for the Application dropdown. */
  applications: ApplicationViewModel[] = [];

  /** True while the BU/Application dropdown options are being fetched. */
  loadingOptions$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly dialogRef: MatDialogRef<SpecificationAddEditComponent, boolean>,
    private readonly esraApiClient: EsraPipingAPIClient,
    private readonly snackBarService: SnackBarService,
    private readonly formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) private readonly specification: SpecificationViewModel | null
  ) {
    this.inputFormGroup = this.formBuilder.group({
      specificationNumber: ['', Validators.required],
      specificationTitle: ['', Validators.required],
      businessUnitId: [null, Validators.required],
      applicationId: [null, Validators.required],
      type: [null, Validators.required],
      name: [''],
    });
  }

  ngOnInit(): void {
    this.specificationToUpdate = this.specification ? new SpecificationViewModel(this.specification) : null;
    this.isEditMode = this.specificationToUpdate !== null;
    this.isCreateMode = !this.isEditMode;

    if (this.isCreateMode) {
      this.title = 'Create a new specification';
    }

    if (this.specificationToUpdate) {
      // Use specificationNumber as the primary display identifier; fall back to name for legacy records.
      const displayName: string = this.specificationToUpdate.specificationNumber || this.specificationToUpdate.name || '';
      this.title = `Edit ${displayName}`;
    }

    // Load BU and Application dropdown options in parallel before populating form values
    // so the selected values are present in the lists when the form is patched.
    combineLatest([
      this.esraApiClient.getAllCurrentBusinessUnits(),
      this.esraApiClient.getAllCurrentApplications(),
    ])
      .pipe(indicate(this.loadingOptions$), first())
      .subscribe({
        next: ([businessUnits, applications]) => {
          this.businessUnits = businessUnits;
          this.applications = applications;
          if (this.isEditMode) {
            this.setFormValues();
          }
        },
        error: () => {
          this.snackBarService.showError('Failed to load Business Unit and Application options.');
          // Still populate form values so the user can at least see the existing data.
          if (this.isEditMode) {
            this.setFormValues();
          }
        },
      });
  }

  setFormValues(): void {
    if (!this.specificationToUpdate) {
      return;
    }

    this.inputFormGroup.setValue({
      specificationNumber: this.specificationToUpdate.specificationNumber || '',
      specificationTitle:  this.specificationToUpdate.specificationTitle  || '',
      businessUnitId:      this.specificationToUpdate.businessUnitId      || null,
      applicationId:       this.specificationToUpdate.applicationId       || null,
      type:                this.specificationToUpdate.type ?? null,
      name:                this.specificationToUpdate.name                || '',
    });
  }

  save(): void {
    this.submitClicked = true;
    this.apiErrorMessage = null;

    if (this.inputFormGroup.invalid) {
      this.inputFormGroup.markAllAsTouched();
      return;
    }

    const specNumber: string = this.inputFormGroup.controls['specificationNumber'].value;
    const payload: SpecificationViewModel = new SpecificationViewModel({
      specificationNumber: specNumber,
      specificationTitle: this.inputFormGroup.controls['specificationTitle'].value,
      businessUnitId: this.inputFormGroup.controls['businessUnitId'].value,
      applicationId: this.inputFormGroup.controls['applicationId'].value || undefined,
      type: this.inputFormGroup.controls['type'].value ?? undefined,
      // For new records name mirrors specificationNumber for backward compat; for edits
      // preserve an existing legacy name if the user typed one, otherwise mirror specNumber.
      name: this.inputFormGroup.controls['name'].value || specNumber,
      isActive: true,
    });

    if (this.isCreateMode) {
      this.esraApiClient.createSpecification(payload)
        .pipe(indicate(this.saving$), first())
        .subscribe({
          next: () => {
            this.snackBarService.showSnackBar(
              true,
              `The new specification "${payload.specificationNumber}" was successfully created.`
            );
            this.dialogRef.close(true);
          },
          error: (err) => { this.handleSaveError(err); },
        });
    }

    const specificationToUpdate: SpecificationViewModel | null = this.specificationToUpdate;
    if (this.isEditMode && specificationToUpdate) {
      // Merge changed fields onto the existing record so that server-managed
      // fields (id, isReferenced, updated, etc.) are preserved on the update payload.
      const updatePayload: SpecificationViewModel = new SpecificationViewModel({
        ...specificationToUpdate,
        specificationNumber: payload.specificationNumber,
        specificationTitle:  payload.specificationTitle,
        businessUnitId:      payload.businessUnitId,
        applicationId:       payload.applicationId,
        type:                payload.type,
        name:                payload.name,
      });
      this.esraApiClient.updateSpecification(updatePayload)
        .pipe(indicate(this.saving$), first())
        .subscribe({
          next: () => {
            this.snackBarService.showSnackBar(
              true,
              `Specification "${updatePayload.specificationNumber}" was successfully updated.`
            );
            this.dialogRef.close(true);
          },
          error: (err) => { this.handleSaveError(err); },
        });
    }
  }

  /**
   * Parses the API error response and either shows an inline field-level
   * message for known validation failures (duplicate SpecificationNumber) or
   * falls back to a snackbar for unexpected errors.
   * @param err The error emitted by the NSwag-generated API client.
   */
  private handleSaveError(err: any): void {
    const responseText: string = this.extractErrorText(err).toLowerCase();
    // Detect the duplicate-specificationNumber server validation response.
    if (responseText.includes('specificationnumber') || responseText.includes('already exists') || responseText.includes('unique')) {
      this.apiErrorMessage = 'This specification number already exists. Please use a unique number.';
    } else {
      this.snackBarService.showSnackBar(
        true,
        this.extractErrorText(err) || 'An unexpected error occurred. Please try again.',
        'mat-snack-bar-error'
      );
    }
  }

  /**
   * Extracts a human-readable error string from the various shapes
   * NSwag-generated client errors can take.
   * @param err The raw error value from an Observable error callback.
   * @returns A plain string message, or empty string if nothing useful is found.
   */
  private extractErrorText(err: any): string {
    if (typeof err === 'string') { return err; }
    if (typeof err?.response === 'string' && err.response) { return err.response; }
    if (typeof err?.message === 'string' && err.message) { return err.message; }
    return '';
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }
}
